import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

const CACHE_TTL = 60 * 60 * 1000;
function getCache(key: string): any {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL) { localStorage.removeItem(key); return null; }
    return data;
  } catch { return null; }
}
function setCache(key: string, data: any): void {
  try { localStorage.setItem(key, JSON.stringify({ data, ts: Date.now() })); } catch {}
}

@Injectable({ providedIn: 'root' })
export class PrintFrameService {
  // New Laravel backend. The ENTIRE Print & Frame flow reads from here now — the
  // price quote, every option master, the init aggregator, the dynamic frame
  // filter, the featured-frames rail and the landing-page settings. The old backend
  // is no longer called from this service.
  private newBase = environment.newApiUrl;

  constructor(private http: HttpClient) {}

  // Featured frames (on_sale)
  getFeaturedFrames(page = 0, size = 20): Observable<any> {
    const filter = JSON.stringify([['status', '=', 1]]);
    const params = new HttpParams()
      .set('filter', filter)
      .set('orderBy', JSON.stringify([['id', 'desc']]))
      .set('pageNumber', String(page))
      .set('pageSize', String(size));
    return this.http.get<any>(`${this.newBase}/api/site/frames/featured`, { params });
  }

  getFeaturedFrame(id: number): Observable<any> {
    return this.http.get<any>(`${this.newBase}/api/site/frames/featured/${id}`);
  }

  // Settings (slides, banners, services)
  getSettings(): Observable<any> {
    const cached = getCache('ma_pf_settings');
    if (cached) return of(cached);
    return this.http.get<any>(`${this.newBase}/api/site/frames/settings`)
      .pipe(tap(res => setCache('ma_pf_settings', res)));
  }

  // Frames
  getFrames(filter: any[] = [], page = 0, size = -1): Observable<any> {
    const params = new HttpParams()
      .set('filter', JSON.stringify(filter))
      .set('orderBy', '[]')
      .set('pageNumber', String(page))
      .set('pageSize', String(size));
    return this.http.get<any>(`${this.newBase}/api/site/frames/masters/frame`, { params });
  }

  // Frame colors
  getFrameColors(): Observable<any> {
    const cached = getCache('ma_frame_colors');
    if (cached) return of(cached);
    const params = new HttpParams()
      .set('filter', JSON.stringify([['status', '=', 1]]))
      .set('orderBy', '[]').set('pageNumber', '0').set('pageSize', '-1');
    return this.http.get<any>(`${this.newBase}/api/site/frames/masters/framecolor`, { params })
      .pipe(tap(res => setCache('ma_frame_colors', res)));
  }

  // Frame widths
  getFrameWidths(): Observable<any> {
    const cached = getCache('ma_frame_widths');
    if (cached) return of(cached);
    const params = new HttpParams()
      .set('filter', JSON.stringify([['status', '=', 1]]))
      .set('orderBy', '[]').set('pageNumber', '0').set('pageSize', '-1');
    return this.http.get<any>(`${this.newBase}/api/site/frames/masters/framewidth`, { params })
      .pipe(tap(res => setCache('ma_frame_widths', res)));
  }

  // Product types
  getProductTypes(): Observable<any> {
    const cached = getCache('ma_product_types');
    if (cached) return of(cached);
    const params = new HttpParams()
      .set('filter', '[]').set('orderBy', '[]')
      .set('pageNumber', '0').set('pageSize', '-1');
    return this.http.get<any>(`${this.newBase}/api/site/frames/masters/product_type`, { params })
      .pipe(tap(res => setCache('ma_product_types', res)));
  }

  // Mount options
  getMounts(): Observable<any> {
    const params = new HttpParams()
      .set('filter', JSON.stringify([['status', '=', 1]]))
      .set('orderBy', '[]').set('pageNumber', '0').set('pageSize', '-1');
    return this.http.get<any>(`${this.newBase}/api/site/frames/masters/mount`, { params });
  }

  // Glass types
  getGlasses(): Observable<any> {
    const params = new HttpParams()
      .set('filter', JSON.stringify([['status', '=', 1]]))
      .set('orderBy', '[]').set('pageNumber', '0').set('pageSize', '-1');
    return this.http.get<any>(`${this.newBase}/api/site/frames/masters/glass`, { params });
  }

  // Canvas wrap options
  getCanvasWraps(): Observable<any> {
    const params = new HttpParams()
      .set('filter', JSON.stringify([['status', '=', 1]]))
      .set('orderBy', '[]').set('pageNumber', '0').set('pageSize', '-1');
    return this.http.get<any>(`${this.newBase}/api/site/frames/masters/canvas_wrap`, { params });
  }

  // Products
  getProducts(): Observable<any> {
    const cached = getCache('ma_pf_products');
    if (cached) return of(cached);
    const params = new HttpParams()
      .set('filter', JSON.stringify([['status', '=', 'Active']]))
      .set('orderBy', JSON.stringify([['name', 'asc']]))
      .set('pageNumber', '0').set('pageSize', '100');
    return this.http.get<any>(`${this.newBase}/api/site/frames/masters/product`, { params })
      .pipe(tap(res => setCache('ma_pf_products', res)));
  }

  // Papers (Print Surfaces)
  getPapers(): Observable<any> {
    const cached = getCache('ma_pf_papers');
    if (cached) return of(cached);
    const params = new HttpParams()
      .set('filter', JSON.stringify([['status', '=', 'Active']]))
      .set('orderBy', JSON.stringify([['name', 'asc']]))
      .set('pageNumber', '0').set('pageSize', '100');
    return this.http.get<any>(`${this.newBase}/api/site/frames/masters/paper`, { params })
      .pipe(tap(res => setCache('ma_pf_papers', res)));
  }

  // Mat/Mount colors
  getMatColors(): Observable<any> {
    const cached = getCache('ma_mat_colors');
    if (cached) return of(cached);
    const params = new HttpParams()
      .set('filter', JSON.stringify([['status', '=', 1]]))
      .set('orderBy', '[]').set('pageNumber', '0').set('pageSize', '-1');
    return this.http.get<any>(`${this.newBase}/api/site/frames/masters/matcolor`, { params })
      .pipe(tap(res => setCache('ma_mat_colors', res)));
  }

  // Mat/Mount widths
  getMatWidths(): Observable<any> {
    const cached = getCache('ma_mat_widths');
    if (cached) return of(cached);
    const params = new HttpParams()
      .set('filter', JSON.stringify([['status', '=', 1]]))
      .set('orderBy', '[]').set('pageNumber', '0').set('pageSize', '-1');
    return this.http.get<any>(`${this.newBase}/api/site/frames/masters/matwidth`, { params })
      .pipe(tap(res => setCache('ma_mat_widths', res)));
  }

  // Price calculation — migrated to the new backend (Phase 1). POSTs to
  // /api/site/frames/quote, which runs the SAME FramePricingService the admin panel
  // prices from, so the product page and the panel cannot disagree on a price.
  // Response shape is unchanged ({ data, restriction } on success, { error } on an
  // unpriceable config), so the component reads it exactly as before.
  getProductPrice(priceParams: any): Observable<any> {
    // Drop null/undefined so the backend sees a clean payload (it treats a 0/absent
    // component id as "none").
    const body: Record<string, any> = {};
    Object.keys(priceParams).forEach(key => {
      if (priceParams[key] !== undefined && priceParams[key] !== null) {
        body[key] = priceParams[key];
      }
    });
    return this.http.post<any>(`${this.newBase}/api/site/frames/quote`, body);
  }

  // Dynamic frame filter options (which materials/styles/colors are valid for current width)
  getCustomizedFrames(widthIn: string, materialId: number, styleId: number): Observable<any> {
    const params = new HttpParams()
      .set('width_in', widthIn)
      .set('frame_material_id', String(materialId))
      .set('frame_style_id', String(styleId));
    return this.http.get<any>(`${this.newBase}/api/site/frames/customized-frames`, { params });
  }

  // THE KEY API — returns everything for the product customization page in one call
  initProductCustomization(product: string, productType: string, userId = 0, imageId = ''): Observable<any> {
    const cacheKey = `ma_init_pc_${product}_${productType}_${imageId}`;
    const cached = getCache(cacheKey);
    // if (cached) return of(cached);
    let params = new HttpParams()
      .set('product', product)
      .set('product_type', productType)
      .set('user_id', String(userId));
    if (imageId) {
      params = params.set('image_id', imageId);
    }
    return this.http.get<any>(`${this.newBase}/api/site/frames/init`, { params })
      .pipe(tap(res => setCache(cacheKey, res)));
  }
}
